import { useCan } from '@features/auth';
import { type ClientTarget } from '@features/clients';
import { WriteError } from '@shared/ui';

import { useRefresh } from '../hooks/use-bulk-migrations';
import { finalReportFileName, toCsv, toJsonDocument } from '../model/final-report-export';
import { isActive } from '../model/job-progress';
import { moduleReportView } from '../model/module-report';
import { finalReportPath, latestReportOf } from '../model/navigation';
import { reportObservations } from '../model/report-observations';
import {
    reportEyebrow,
    reportFooter,
    reportLede,
    reportMeta,
    reportTitle,
    summaryCells,
} from '../model/report-overview';
import { notMigratedGroups, sheetSections } from '../model/report-sections';

import { ClientSheet } from './ClientSheet';
import { downloadCsv, downloadJson } from './download';
import { NotMigratedSection } from './NotMigratedSection';
import { ReportHeader } from './ReportHeader';
import { PartialBanner, ReportSummaryStrip } from './ReportSummary';

import type { FinalReport } from '../model/final-report';
import type { Observation } from '../model/module-report-types';
import type { BulkModule, JobHeader } from '../schemas/bulk-migration';

/**
 * The final report laid out as one document in a single centred column:
 * header, summary strip, one sheet per migrated client, the clients not
 * migrated, observations, footer. Generic — what is module-specific (a
 * record's mapping, its preview, its observations) comes from the module's
 * entry in `moduleReportView`.
 */

/** A section heading, never left alone at the foot of a printed page. */
function SectionTitle({ id, children }: { id: string; children: string }) {
    return (
        <h2
            id={id}
            className="text-[20px] font-semibold tracking-tight text-content print:break-after-avoid"
        >
            {children}
        </h2>
    );
}

function Observations({ items }: { items: readonly Observation[] }) {
    return (
        <section aria-labelledby="observations" className="grid gap-3.5">
            <SectionTitle id="observations">Observations</SectionTitle>
            <ul className="border-t border-line">
                {items.map((item, index) => (
                    <li
                        key={`${item.label}-${String(index)}`}
                        className="grid gap-x-5 gap-y-1 border-b border-line py-3.5 sm:grid-cols-[120px_minmax(0,1fr)]"
                    >
                        <h3 className="pt-0.5 text-[12px] font-semibold uppercase tracking-wider text-content-muted">
                            {item.label}
                        </h3>
                        <p className="max-w-[72ch] text-[14px] text-content-body">
                            {item.code && (
                                <span className="mr-1.5 font-mono text-[12px] text-content-subtle">
                                    {item.code}
                                </span>
                            )}
                            {item.text}
                        </p>
                    </li>
                ))}
            </ul>
        </section>
    );
}

export function ReportDocument({
    target,
    module,
    report,
    jobs,
    launchReportId,
}: {
    target: ClientTarget;
    module: BulkModule;
    report: FinalReport;
    jobs: readonly JobHeader[];
    launchReportId: string;
}) {
    const canRefresh = useCan('engineer');
    const refresh = useRefresh(target, module.moduleId, launchReportId);
    const latest = latestReportOf(jobs, launchReportId);
    const refreshing = refresh.isPending || (latest !== undefined && isActive(latest.state));

    const view = moduleReportView(module.moduleId);
    const sections = sheetSections(report);
    const groups = notMigratedGroups(report);
    const observations = reportObservations(report, view);
    const sharePath = finalReportPath(target, module.moduleId, launchReportId);

    return (
        <article className="mx-auto grid w-full max-w-[1040px] gap-9 pb-16 pt-2 print:max-w-none print:gap-7">
            <ReportHeader
                eyebrow={reportEyebrow(report)}
                title={reportTitle(report)}
                lede={reportLede(report, view)}
                meta={reportMeta(report)}
                shareUrl={`${window.location.origin}${sharePath}`}
                onPrint={() => {
                    window.print();
                }}
                onCsv={() => {
                    downloadCsv(finalReportFileName(report, 'csv'), toCsv(report, module.columns));
                }}
                onJson={() => {
                    downloadJson(finalReportFileName(report, 'json'), toJsonDocument(report));
                }}
            />

            <ReportSummaryStrip cells={summaryCells(report)} />

            {!report.header.isFinal && (
                <PartialBanner
                    unsettled={report.header.unsettled}
                    refreshing={refreshing}
                    canRefresh={canRefresh}
                    onRefresh={() => {
                        refresh.mutate();
                    }}
                />
            )}
            {refresh.error && (
                <div className="print:hidden">
                    <WriteError error={refresh.error} />
                </div>
            )}

            {sections.map((section) => {
                const [first, ...rest] = section.sheets;
                const sheet = (item: typeof first) =>
                    item && (
                        <ClientSheet
                            key={item.key}
                            sheet={item}
                            view={view}
                            columns={module.columns}
                        />
                    );
                return (
                    <section
                        key={section.key}
                        aria-labelledby={`sheets-${section.key}`}
                        className="grid gap-7"
                    >
                        {/* The heading and its first sheet print together. */}
                        <div className="grid gap-4 print:break-inside-avoid">
                            <SectionTitle id={`sheets-${section.key}`}>
                                {`${section.title} · ${String(section.clients)}`}
                            </SectionTitle>
                            {sheet(first)}
                        </div>
                        {rest.map(sheet)}
                    </section>
                );
            })}

            {groups.length > 0 && <NotMigratedSection groups={groups} columns={module.columns} />}

            {observations.length > 0 && <Observations items={observations} />}

            <footer className="grid gap-1 text-content-muted">
                {reportFooter(report).map((line) => (
                    <span key={line} className="font-mono text-[11.5px] [overflow-wrap:anywhere]">
                        {line}
                    </span>
                ))}
            </footer>
        </article>
    );
}
